import React from "react";
import {
  Box,
  Typography,
  Grid,
  Alert,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { AnnouncementResponseDto } from "../../shared/dtos/announcement/AnnouncementResponseDto";

interface ViewAnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  announcement: AnnouncementResponseDto | null;
  loading: boolean;
  error: string | null;
}

const ViewAnnouncementModal: React.FC<ViewAnnouncementModalProps> = ({
  open,
  onClose,
  announcement,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;
    if (error)
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    if (!announcement)
      return <Typography sx={{ my: 2 }}>Data not available.</Typography>;

    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
          {announcement.title}
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            my: 2,
            bgcolor: "background.default",
            borderRadius: 1,
            borderStyle: "dashed",
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {announcement.content}
          </Typography>
        </Paper>

        <Grid container spacing={2}>
          <DetailItem label="Author" value={announcement.authorName} />
          <DetailItem
            label="Audience Type"
            value={announcement.audience.replace(/_/g, " ")}
          />
          <DetailItem
            label="Publish Date"
            value={
              announcement.publishDate
                ? new Date(announcement.publishDate).toLocaleString()
                : "Immediate"
            }
          />
          <DetailItem
            label="Expiry Date"
            value={
              announcement.expiryDate
                ? new Date(announcement.expiryDate).toLocaleString()
                : "N/A"
            }
          />
        </Grid>

        {announcement.targetBranches?.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Targeted Branches
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {announcement.targetBranches.map((b) => (
                <Chip
                  key={b.id}
                  label={b.name}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>
          </Box>
        )}

        {announcement.targetRoles?.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Targeted Roles
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {announcement.targetRoles.map((r) => (
                <Chip
                  key={r.id}
                  label={r.name}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Announcement Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewAnnouncementModal;
